import { useEffect, useState } from 'react';

import type { ChallengeStimulus } from '../api/models';
import { AppText } from '../components/AppText';
import { Motion } from '../theme/space';
import { GridView, SymbolView } from '../symbols/SymbolView';

/**
 * What the child is shown, chosen by `stimulus.kind`.
 * Mirrors android/…/Challenges/StimulusView.kt.
 *
 * This is the **second** switch in the shell and it is independent of the presentation: a `multiple_choice`
 * task can carry a symbol, a sequence, a story or a grid. Folding the two switches together would mean
 * adding a stimulus kind broke an unrelated answer control (manifest §6a).
 */
/**
 * What makes this stimulus a different one from the last.
 *
 * The timer restarts on it, so it has to change when the task does. The symbols alone are not enough: a
 * grid carries its objects in `grid.cells` and may have no `symbols` at all, and two tasks in a row that
 * both had none would share a key and the second would never re-show.
 */
const contentKey = (stimulus: ChallengeStimulus): string =>
  [
    stimulus.kind,
    (stimulus.symbols ?? []).join('|'),
    (stimulus.grid?.cells ?? []).join('|'),
    stimulus.text ?? '',
  ].join('::');

export function StimulusView({ stimulus }: { stimulus: ChallengeStimulus }) {
  const symbols = stimulus.symbols ?? [];

  const content = (() => {
    switch (stimulus.kind) {
      case 'symbol':
        return <SymbolView token={symbols[0] ?? ''} size={96} />;
      case 'symbol_sequence':
      case 'digit_sequence':
        return <SymbolRow symbols={symbols} />;
      case 'grid':
        return stimulus.grid ? <GridView grid={stimulus.grid} cellSize={44} /> : null;
      case 'text':
        return <StoryText stimulus={stimulus} />;
      default:
        return stimulus.text ? (
          <StoryText stimulus={stimulus} />
        ) : (
          <SymbolView token={symbols[0] ?? ''} size={96} />
        );
    }
  })();

  // Centred, which is where Android and iOS have always put it.
  //
  // R0106 moved this one to the left so a pattern would line up with the instruction and the options, and
  // changed only the web — so for a while the same task was laid out two ways depending on the device, and
  // `check-parity.sh` could not see it because it compares inventories and not layout. On a phone the
  // result read as neither aligned nor centred: the sequence sits in a grid as wide as its own content, so
  // its placeholder landed at an offset that belonged to nothing (R0125).
  //
  // The stimulus was `aria-hidden` all the way down, so the task was invisible to a screen reader while
  // `10-Accessibility.md` §5 said exactly what it should announce. The server composes the sentence; this
  // is where it is spoken (R0117).
  return (
    <div
      style={{ width: '100%', display: 'flex', justifyContent: 'center' }}
      role="img"
      aria-label={
        stimulus.accessibleName !== undefined && stimulus.accessibleName.length > 0
          ? stimulus.accessibleName
          : undefined
      }
    >
      <TimedExposure exposureMs={stimulus.exposureMs ?? 0} resetKey={contentKey(stimulus)}>
        {content}
      </TimedExposure>
    </div>
  );
}

/**
 * Anything that must be memorised, then hidden.
 *
 * **The exposure is honoured here, for every stimulus kind, and not inside one of them.** It used to live
 * in the sequence renderer, so `symbol_sequence` and `digit_sequence` disappeared and a `grid` did not —
 * and *What disappeared* is a grid. Its objects stayed on screen while it asked which one had gone, which
 * is the whole task defeated (R0149).
 *
 * `exposureMs` of zero means the stimulus is not timed at all, which is every other grid task, so it never
 * hides and the placeholder is never drawn.
 *
 * **No countdown, no timer, no progress ring** — BR-12 forbids showing a child the clock. It simply fades
 * when its time is up. The child is never told how long they have, because being told is what turns a
 * memory task into a stress test.
 */
function TimedExposure({
  exposureMs,
  resetKey,
  children,
}: {
  exposureMs: number;
  resetKey: string;
  children: React.ReactNode;
}) {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    setIsVisible(true);

    if (exposureMs <= 0) return;

    const handle = window.setTimeout(() => setIsVisible(false), exposureMs);

    return () => window.clearTimeout(handle);
  }, [resetKey, exposureMs]);

  if (exposureMs <= 0) {
    // Nothing to time, and nothing to lay out around: an untimed stimulus keeps exactly the box it had
    // before this wrapper existed.
    return <>{children}</>;
  }

  return (
    <div style={{ display: 'grid', placeItems: 'center' }}>
      <div
        style={{
          gridArea: '1 / 1',
          opacity: isVisible ? 1 : 0,
          transition: `opacity ${Motion.exposureFade}ms ease`,
        }}
      >
        {children}
      </div>

      {isVisible ? null : (
        <div
          style={{
            gridArea: '1 / 1',
            // Fills the cell the stimulus left behind rather than sizing to three dots, so the box does
            // not jump inward at the moment it disappears — which is the moment a child is looking
            // hardest at it (R0125).
            justifySelf: 'stretch',
            alignSelf: 'stretch',
            display: 'grid',
            placeItems: 'center',
            borderRadius: 'var(--radius-m)',
            background: 'var(--surface-sunken)',
            padding: 'var(--space-5) var(--space-6)',
          }}
        >
          <AppText style="titleM" color="var(--text-secondary)">
            •&nbsp;&nbsp;•&nbsp;&nbsp;•
          </AppText>
        </div>
      )}
    </div>
  );
}

/** The symbols of a sequence, side by side. The hiding is the wrapper's business, not this one's. */
function SymbolRow({ symbols }: { symbols: Array<string> }) {
  return (
    <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
      {symbols.map((token: string, index: number) => (
        <SymbolView key={`${token}-${index}`} token={token} size={64} />
      ))}
    </div>
  );
}

/** A story, or the words of a sentence-assembly task. */
function StoryText({ stimulus }: { stimulus: ChallengeStimulus }) {
  const body = stimulus.text ?? null;
  const symbols = stimulus.symbols ?? [];

  return (
    <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
      {body !== null ? (
        <div
          style={{
            width: '100%',
            borderRadius: 'var(--radius-m)',
            background: 'var(--surface-raised)',
            padding: 'var(--space-5)',
          }}
        >
          {/*
            The title is its own element, heavier and larger, on its own line. It used to arrive inside
            `text` as `title\n\nbody` and render as one paragraph — „Dážď a dúha Celé doobedie pršalo…" —
            so the story had no title a child could see (R0094).
          */}
          {stimulus.title ? (
            <AppText as="h2" style="titleM" sx={{ marginBottom: 'var(--space-3)' }}>
              {stimulus.title}
            </AppText>
          ) : null}

          {/*
            Justified, because this is the one block of prose in the product a child reads rather than
            scans — a ragged right edge on a narrow column costs more here than anywhere else (R0143).
          */}
          <AppText style="bodyL" align="justify">
            {body}
          </AppText>
        </div>
      ) : null}

      {body === null && symbols.length > 0 ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
          {symbols.map((word: string, index: number) => (
            <span
              key={`${word}-${index}`}
              style={{
                borderRadius: 'var(--radius-pill)',
                background: 'var(--surface-sunken)',
                padding: 'var(--space-2) var(--space-3)',
                color: 'var(--text-secondary)',
              }}
            >
              {word}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}
